/**
 * TroiaReadingPane - Painel de leitura nativo da plataforma
 * Toolbar com icon buttons padrão + Tooltip shared (F80)
 * Thread messages colapsáveis, attachments
 */

import React, { useState } from 'react';
import type { ConversationResponse, ConversationMessageResponse } from '@troia-v3/types';
import { useQuery } from '@tanstack/react-query';
import { getEmailMessages } from '../../shared/services/emailService';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { EmailHtmlFrame } from '@/shared/components/ui/EmailHtmlFrame';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { HEADER_HEIGHT } from '@/shared/constants/layout';
import { TroiaEmptyState } from './TroiaEmptyState';
import { Icon } from '@/shared/icons'
type SidebarTab = 'inbox' | 'sent' | 'drafts' | 'spam' | 'trash' | 'archive';

interface TroiaReadingPaneProps {
  conversation: ConversationResponse | null;
  activeTab: SidebarTab;
  onBack: () => void;
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
  /** Mover para a lixeira (botão de excluir fora da Lixeira). */
  onDelete?: () => void;
  /** Excluir de vez — só a Lixeira mostra, e só depois da confirmação. */
  onPermanentDelete?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onStar?: () => void;
  onMarkSpam?: () => void;
  onUnspam?: () => void;
  onRestore?: () => void;
  onMarkUnread?: () => void;
}

// Nota: `EmailHtmlFrame` (iframe sandboxed sem allow-scripts + CSP) foi
// extraído para `@/shared/components/ui/EmailHtmlFrame.tsx` (Spec A Task 8)
// — reusado pela timeline do ticket e pelo Plano B (Retenção). Modelo de
// segurança preservado 1:1, ver comentário no arquivo shared.

// Avatar sem foto usa slate fixo (#64748b) — padrão do Design System.
const AVATAR_SLATE = '#64748b';

const ThreadMessage: React.FC<{
  message: ConversationMessageResponse;
  isLast: boolean;
  senderName: string;
  senderAvatar?: string;
}> = ({ message, isLast, senderName, senderAvatar }) => {
  const [expanded, setExpanded] = useState(isLast);

  type ContentItem = { type: string; text?: string; url?: string; filename?: string; size?: number; mimeType?: string };
  const contentItems = (message.content as ContentItem[]) || [];
  const rawText = contentItems.find((c) => c.type === 'text')?.text || '';
  const docAttachments = contentItems.filter((c) => c.type === 'document');
  const htmlBody = message.html;
  const plainTextHtml = rawText ? rawText.replace(/\n/g, '<br>') : '';
  const date = message.createdAt || '';

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const initials = senderName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      className="border-b last:border-b-0"
      style={{ borderColor: 'var(--theme-card-border)' }}
    >
      <button
        data-testid={`thread-message-toggle-${message.id}`}
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3.5 min-[900px]:px-6 text-left transition-colors"
        onMouseEnter={(e) => {
          if (!expanded) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Avatar className="w-10 h-10 flex-shrink-0" isSquircle>
          {senderAvatar ? (
            <AvatarImage src={senderAvatar} alt={senderName} isSquircle />
          ) : (
            <AvatarFallback
              isSquircle
              className="text-xs font-bold"
              style={{ backgroundColor: AVATAR_SLATE, color: '#ffffff' }}
            >
              {initials}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1 min-w-0">
          {/* Celular: nome em cima, data embaixo. Lado a lado, a data completa
              ("25 de ago. de 2026, 03:56") comia a linha e o remetente virava
              "e2e…" num aparelho de 390px. */}
          <div className="flex flex-col items-start gap-0 min-[900px]:flex-row min-[900px]:items-center min-[900px]:gap-2 min-w-0">
            <div className="flex items-center gap-2 max-w-full min-w-0">
              <span className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
                {senderName}
              </span>
              {docAttachments.length > 0 && (
                <Icon name="paperclip" className="h-3 w-3 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
              )}
            </div>
            <span className="text-xs flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
              {formatDate(date)}
            </span>
          </div>
          {!expanded && (
            <p className="text-xs truncate mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
              {rawText.replace(/<[^>]*>/g, '').substring(0, 100)}
            </p>
          )}
        </div>
        <div
          className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {expanded ? (
            <Icon name="chevron-up" className="h-4 w-4" />
          ) : (
            <Icon name="chevron-down" className="h-4 w-4" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-5 min-[900px]:px-6 min-[900px]:pl-[80px]">
          {htmlBody ? (
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
              <EmailHtmlFrame html={htmlBody} />
            </div>
          ) : plainTextHtml ? (
            <div
              className="prose max-w-none text-sm rounded-xl p-4 leading-relaxed"
              style={{ backgroundColor: '#ffffff', color: '#1e293b', border: '1px solid #e5e7eb' }}
              dangerouslySetInnerHTML={{ __html: plainTextHtml }}
            />
          ) : (
            <div
              className="text-sm italic rounded-xl p-4"
              style={{ backgroundColor: '#ffffff', color: '#94a3b8', border: '1px solid #e5e7eb' }}
            >
              Sem conteúdo disponível
            </div>
          )}

          {docAttachments.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Icon name="paperclip" className="h-3.5 w-3.5" style={{ color: 'var(--theme-muted-text)' }} />
                <span className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
                  {docAttachments.length} anexo{docAttachments.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {docAttachments.map((att, i) => (
                  <a
                    key={i}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2 rounded-xl border text-xs transition-colors"
                    style={{
                      borderColor: 'var(--theme-card-border)',
                      backgroundColor: 'var(--theme-card-background)',
                      color: 'var(--theme-card-text)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-card-background)';
                    }}
                  >
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: 'var(--theme-button-ghost-hover)' }}
                    >
                      <Icon name="download" className="h-4 w-4" style={{ color: 'var(--theme-button-primary)' }} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate max-w-[160px] font-medium">{att.filename || 'Arquivo'}</div>
                      {att.size != null && (
                        <div style={{ color: 'var(--theme-muted-text)' }}>{formatSize(att.size)}</div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const TroiaReadingPane: React.FC<TroiaReadingPaneProps> = ({
  conversation,
  activeTab,
  onBack,
  onReply,
  onReplyAll,
  onForward,
  onDelete,
  onPermanentDelete,
  onArchive,
  onUnarchive,
  onStar,
  onMarkSpam,
  onUnspam,
  onRestore,
  onMarkUnread,
}) => {
  // Confirmação de exclusão permanente pelo primitivo do DS (`useConfirm`) —
  // o diálogo feito à mão que existia aqui tinha `w-[400px]` fixo (não cabia num
  // celular de 390px) e z-index fora da escala (`z-[9998]/[9999]`).
  const confirm = useConfirm();
  const askPermanentDelete = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Excluir permanentemente?',
      message: 'Este e-mail será excluído permanentemente. Esta ação não pode ser desfeita.',
      confirmButtonText: 'Excluir permanente',
      variant: 'danger',
    });
    if (ok) onPermanentDelete?.();
  };

  const { data: messagesData } = useQuery({
    queryKey: ['email-messages', conversation?.id],
    queryFn: ({ signal }) => getEmailMessages(conversation!.id, 1, 50, signal),
    enabled: !!conversation?.id,
    staleTime: 30 * 1000,
  });

  const messages = messagesData?.items || [];

  if (!conversation) {
    return (
      <div
        className="flex-1 flex flex-col"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <TroiaEmptyState type="no-selection" />
      </div>
    );
  }

  const subject = conversation.subject || '(Sem assunto)';
  const senderName = conversation.contact?.name || 'Sem remetente';
  const senderAvatar = conversation.contact?.picture;
  const isStarred = conversation.tags?.includes('starred');

  // Mobile: mockup `.em-ract` mostra só 3 ícones (Responder/Arquivar/Excluir);
  // o restante (Todos/Encaminhar/Favoritar/Spam/Não lido) vai pro ⋮ (ActionSheet)
  // pra não perder função. Ações contextuais por pasta.
  const mobileMoreActions: CardAction[] = [
    { label: 'Responder a todos', icon: 'users', onClick: () => onReplyAll?.() },
    { label: 'Encaminhar', icon: 'corner-up-right', onClick: () => onForward?.() },
    { label: isStarred ? 'Remover favorito' : 'Favoritar', icon: 'star', onClick: () => onStar?.() },
    ...(activeTab === 'spam'
      ? [{ label: 'Não é spam', icon: 'inbox' as const, onClick: () => onUnspam?.() }]
      : activeTab !== 'trash'
        ? [{ label: 'Marcar como spam', icon: 'alert-octagon' as const, onClick: () => onMarkSpam?.() }]
        : []),
    { label: 'Marcar como não lido', icon: 'eye-off', onClick: () => onMarkUnread?.() },
  ];

  const ToolbarIconButton: React.FC<{
    onClick?: () => void;
    tooltipLabel: string;
    testId?: string;
    className?: string;
    children: React.ReactNode;
  }> = ({ onClick, tooltipLabel, testId, className = '', children }) => (
    <Tooltip content={tooltipLabel} side="bottom">
      <button
        data-testid={testId}
        onClick={() => onClick?.()}
        // Alvo de toque: 44px no celular (mínimo do projeto para tela sensível ao
        // toque), 36px do desktop preservado a partir de 900px.
        className={`items-center justify-center w-11 h-11 min-[900px]:w-9 min-[900px]:h-9 rounded-lg transition-colors ${className || 'flex'}`}
        style={{ color: 'var(--theme-muted-text)' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {children}
      </button>
    </Tooltip>
  );

  return (
    <>
    <div
      data-testid="troia-reading-pane"
      className="flex-1 flex flex-col overflow-hidden max-lg:[background:var(--theme-page-background-color)] lg:[background-color:var(--theme-page-background)]"
    >
      {/* Toolbar */}
      <div
        className="flex items-center justify-between gap-3 px-4 border-b flex-shrink-0"
        style={{
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          borderColor: 'var(--theme-card-border)',
          backgroundColor: 'var(--theme-card-background)',
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ToolbarIconButton onClick={onBack} tooltipLabel="Voltar" testId="reading-pane-back">
            <Icon name="arrow-left" className="h-[18px] w-[18px]" />
          </ToolbarIconButton>

          <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--theme-card-border)' }} />

          <div className="flex flex-col min-w-0">
            <span
              className="text-lg font-bold truncate"
              style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}
            >
              {subject}
            </span>
            <span
              className="text-xs truncate"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {senderName}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Mobile `.em-ract`: Responder — abre o compose float (mesma ação do desktop) */}
          <ToolbarIconButton
            className="flex sm:hidden"
            onClick={onReply}
            tooltipLabel="Responder"
            testId="reading-pane-reply-mobile"
          >
            <Icon name="corner-up-left" className="h-[18px] w-[18px]" />
          </ToolbarIconButton>

          {/* Reply — desktop: botão roxo com label + abre compose float */}
          <button
            data-testid="reading-pane-reply"
            onClick={() => onReply?.()}
            className="hidden sm:flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold transition-colors"
            style={{
              backgroundColor: 'var(--theme-button-primary)',
              color: 'var(--theme-button-primary-text)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)'; }}
          >
            <Icon name="corner-up-left" className="h-4 w-4" />
            <span className="hidden sm:inline">Responder</span>
          </button>

          {/* Reply All — secundário: oculto no mobile */}
          <button
            data-testid="reading-pane-reply-all"
            onClick={() => onReplyAll?.()}
            className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium transition-colors"
            style={{
              borderColor: 'var(--theme-input-border)',
              color: 'var(--theme-card-text)',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Icon name="users" className="h-4 w-4" />
            Todos
          </button>

          {/* Forward — secundário: oculto no mobile */}
          <button
            data-testid="reading-pane-forward"
            onClick={() => onForward?.()}
            className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium transition-colors"
            style={{
              borderColor: 'var(--theme-input-border)',
              color: 'var(--theme-card-text)',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Icon name="corner-up-right" className="h-4 w-4" />
            Encaminhar
          </button>

          <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--theme-card-border)' }} />

          {/* Archive / Unarchive - contextual */}
          {activeTab === 'archive' ? (
            <ToolbarIconButton onClick={onUnarchive} tooltipLabel="Desarquivar" testId="reading-pane-unarchive">
              <Icon name="archive" className="h-[18px] w-[18px]" />
            </ToolbarIconButton>
          ) : activeTab !== 'trash' ? (
            <ToolbarIconButton onClick={onArchive} tooltipLabel="Arquivar" testId="reading-pane-archive">
              <Icon name="archive" className="h-[18px] w-[18px]" />
            </ToolbarIconButton>
          ) : null}

          {/* Star — desktop (mobile vai pro ⋮) */}
          <ToolbarIconButton className="hidden sm:flex" onClick={onStar} tooltipLabel={isStarred ? 'Remover favorito' : 'Favoritar'} testId="reading-pane-star">
            <Icon name="star" className="h-[18px] w-[18px]"
              style={isStarred ? { fill: 'var(--theme-warning-color)', color: 'var(--theme-warning-color)' } : {}} />
          </ToolbarIconButton>

          {/* Spam / Not Spam - contextual — desktop */}
          {activeTab === 'spam' ? (
            <ToolbarIconButton className="hidden sm:flex" onClick={onUnspam} tooltipLabel="Não é spam" testId="reading-pane-unspam">
              <Icon name="inbox" className="h-[18px] w-[18px]" />
            </ToolbarIconButton>
          ) : activeTab !== 'trash' ? (
            <ToolbarIconButton className="hidden sm:flex" onClick={onMarkSpam} tooltipLabel="Marcar como spam" testId="reading-pane-mark-spam">
              <Icon name="alert-octagon" className="h-[18px] w-[18px]" />
            </ToolbarIconButton>
          ) : null}

          {/* Mark as Unread — desktop */}
          <ToolbarIconButton className="hidden sm:flex" onClick={onMarkUnread} tooltipLabel="Marcar como não lido" testId="reading-pane-mark-unread">
            <Icon name="eye-off" className="h-[18px] w-[18px]" />
          </ToolbarIconButton>

          {/* Restore (only in trash) */}
          {activeTab === 'trash' && (
            <ToolbarIconButton onClick={onRestore} tooltipLabel="Restaurar" testId="reading-pane-restore">
              <Icon name="refresh" className="h-[18px] w-[18px]" />
            </ToolbarIconButton>
          )}

          {/* Delete / Permanent Delete - contextual */}
          {activeTab === 'trash' ? (
            <ToolbarIconButton
              onClick={() => { void askPermanentDelete(); }}
              tooltipLabel="Excluir permanentemente"
              testId="reading-pane-delete-permanent"
            >
              <Icon name="trash" className="h-[18px] w-[18px]" style={{ color: 'var(--theme-error-color)' }} />
            </ToolbarIconButton>
          ) : (
            <ToolbarIconButton onClick={onDelete} tooltipLabel="Excluir" testId="reading-pane-delete">
              <Icon name="trash" className="h-[18px] w-[18px]" />
            </ToolbarIconButton>
          )}

          {/* Mobile: ⋮ com ações secundárias (Todos/Encaminhar/Favoritar/Spam/Não lido) */}
          <div className="sm:hidden flex items-center">
            <CardActionsMenu actions={mobileMoreActions} data-testid="reading-pane-more-mobile" />
          </div>
        </div>
      </div>

      {/* Content - full width */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-5">
          {/* Thread messages */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{
              borderColor: 'var(--theme-card-border)',
              backgroundColor: 'var(--theme-card-background)',
            }}
          >
            {messages.length > 0 ? (
              messages.map((msg, idx) => (
                <ThreadMessage
                  key={msg.id}
                  message={msg}
                  isLast={idx === messages.length - 1}
                  senderName={senderName}
                  senderAvatar={senderAvatar}
                />
              ))
            ) : (
              <ThreadMessage
                message={{
                  id: 'preview',
                  content: [{ type: 'text', text: conversation.lastMessage || '' }],
                  createdAt: conversation.lastMessageAt || conversation.createdAt,
                } as ConversationMessageResponse}
                isLast={true}
                senderName={senderName}
                senderAvatar={senderAvatar}
              />
            )}
          </div>

        </div>
      </div>
    </div>

    </>
  );
};

TroiaReadingPane.displayName = 'TroiaReadingPane';
